import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import FlightOutlinedIcon from '@mui/icons-material/FlightOutlined';
import LuggageOutlinedIcon from '@mui/icons-material/LuggageOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import { Box, Button, Container, Stack, Typography } from '@mui/material';
import { ROUTE_KEYS } from 'legacyfrontend/constants';
import { useFlightContext } from 'legacyfrontend/contexts/FlightDetailsContext';
import { formatPrice } from 'legacyfrontend/helpers/formatPrice';
import { generateBookingModel } from 'legacyfrontend/helpers/generateBookingModel';
import { scrollToTop } from 'legacyfrontend/helpers/scrollToTop';
import { useBookingData } from 'legacyfrontend/hooks/useBookingData';
import { useColors } from 'legacyfrontend/hooks/useColors';
import useFlightCodes from 'legacyfrontend/hooks/useFlightInfo';
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import FlightSingleCard from '../FlightSingleCard';
import BaggageForm from './BaggageForm';
import BookingStatusModal from './components/BookingStatusModal';
import FormErrorHandler from './components/FormErrorHandler';
import WarningsModal from './components/WarningsModal';
import ContactForm from './ContactForm';
import CustomAccordion from './CustomAccordion';
import { AttentionAlert, InfoAlert } from './CustomAlert';
import PassengerForm from './PassengerForm';
import PriceBreakdown from './PriceBreakdown';
import { getSessionStorageItem } from 'legacyfrontend/helpers/sessionStorageHelper';
import { safeSessionStorage, useSessionStorage, isClient } from 'legacyfrontend/helpers/ssr-helper';

const useHandleBrowserBackButton = (backToUrl) => {
  const history = useHistory();

  useEffect(() => {
    isClient && window.history.pushState({ preventBack: true }, '');

    const handlePopState = (event) => {
      if (!event.state || !event.state.preventBack) {
        isClient && window.history.pushState({ preventBack: true }, '');
        if (isClient) {
          window.location.href = backToUrl;
        }
      }
    };

    isClient && window.addEventListener('popstate', handlePopState);

    return () => {
      isClient && window.removeEventListener('popstate', handlePopState);
    };
  }, [backToUrl, history]);
};

const useSessionStorageValidation = (requiredKeys) => {
  const history = useHistory();

  useEffect(() => {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      history.push(ROUTE_KEYS.HOME);
      return;
    }

    const missingKeys = requiredKeys.filter((key) => !getSessionStorageItem(key));

    if (missingKeys.length > 0) {
      history.push(ROUTE_KEYS.HOME);
      return;
    }
  }, [history, requiredKeys]);
};

const initialModalState = {
  open: false,
  status: 'loading',
  bookingId: null,
  errorMessage: '',
  emailResponse: null,
};

const BookingPage = () => {
  const {
    handlePricingAndStandalone,
    createBooking,
    flightInfo,
    sendEmailInfo,
    cleanSessionStorage,
  } = useFlightContext();
  const { background, text } = useColors();

  const requiredSessionKeys = ['flightInfo', 'flightSelections', 'selectedFare'];
  useSessionStorageValidation(requiredSessionKeys);

  const [pricingData, setPricingData] = useState(null);
  const [flightExpanded, setFlightExpanded] = useState(true);
  const [contactExpanded, setContactExpanded] = useState(false);
  const [baggageExpanded, setBaggageExpanded] = useState(false);
  const [priceExpanded, setPriceExpanded] = useState(false);
  const [useCommonFiscalData, setUseCommonFiscalData] = useState(false);
  const [commonFiscalDataPassengerIndex, setCommonFiscalDataPassengerIndex] = useState(null);
  const [commonFiscalData, setCommonFiscalData] = useState({
    documentType: '',
    documentNumber: '',
  });
  const [passengersFiscalData, setPassengersFiscalData] = useState([]);
  const [selectedBaggage, setSelectedBaggage] = useState({});
  const [totalPrice, setTotalPrice] = useState(0);
  const [flightType, setFlightType] = useState('national');
  const [warningsModalState, setWarningsModalState] = useState({
    open: false,
    warnings: [],
  });

  const { originCityName, originCode, destinationCityName, destinationCode } =
    useFlightCodes(pricingData);

  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const [isSubmitAttempted, setIsSubmitAttempted] = useState(false);

  const [modalState, setModalState] = useState(initialModalState);

  const backUrlRef = useRef(null);
  const contactFormRef = useRef(null);
  const passengerFormRefs = useRef([]);

  useEffect(() => {
    try {
      const flightInfo = JSON.parse(safeSessionStorage.getItem('flightInfo') || '{}');
      setFlightType(
        flightInfo.originCountryCode === flightInfo.destinationCountryCode
          ? 'national'
          : 'international'
      );
    } catch (error) {
      setFlightType('national');
    }
  }, []);

  useEffect(() => {
    passengerFormRefs.current = Array(flightInfo?.passengers?.length || 0).fill(null);
  }, [flightInfo?.passengers?.length]);

  const handlePassengerFormRef = useCallback(
    (index) => (el) => {
      if (el) {
        passengerFormRefs.current[index] = el;
      }
    },
    []
  );

  const handleCloseModal = useCallback(() => {
    setModalState(initialModalState);
  }, []);

  const handleCloseWarningsModal = () => {
    setWarningsModalState((prev) => ({
      ...prev,
      open: false,
    }));
  };

  const handleAcceptPriceChange = () => {
    const priceChangeWarning = warningsModalState.warnings.find((w) => w.id === 'CHANGE_PRICE');

    if (priceChangeWarning && priceChangeWarning.extensions) {
      const { currentAmount } = priceChangeWarning.extensions;
      setPricingData((prevData) => {
        if (!prevData || !prevData.pricing) return prevData;
        const updatedPricing = {
          ...prevData.pricing,
          price: {
            ...prevData.pricing.price,
            amount: currentAmount,
            total: currentAmount,
          },
        };
        return {
          ...prevData,
          pricing: updatedPricing,
        };
      });
    }

    handleCloseWarningsModal();
  };

  const [expandedPassengers, setExpandedPassengers] = useState([]);

  const handleTotalPriceChange = useCallback((newTotal) => {
    setTotalPrice(newTotal);
  }, []);

  const handleBaggageSelection = useCallback((selection) => {
    setSelectedBaggage(selection);
  }, []);

  const fetchPricingData = useCallback(async () => {
    setIsLoading(true);
    setHasError(false);
    try {
      const result = await handlePricingAndStandalone();
      if (result?.success) {
        setPricingData(result);

        if (result.pricing?.warnings && result.pricing.warnings.length > 0) {
          const priceChangeWarnings = result.pricing.warnings.filter(
            (warning) => warning.id === 'CHANGE_PRICE'
          );

          if (priceChangeWarnings.length > 0) {
            setWarningsModalState({
              open: true,
              warnings: priceChangeWarnings,
            });
          }
        }
      } else {
        setHasError(true);
      }
    } catch (error) {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, [handlePricingAndStandalone]);

  const generatePassengerForms = useMemo(() => {
    const forms = [];
    const passengers = pricingData?.pricing?.passengers;
    if (!passengers) return forms;

    for (let i = 0; i < (passengers?.adults?.quantity || 0); i++) {
      forms.push({
        type: 'Adulto',
        ageDescription: '(+12 años)',
        isAdult: true,
        passengerType: 'ADT',
      });
    }

    for (let i = 0; i < (passengers?.children?.quantity || 0); i++) {
      forms.push({
        type: 'Niño',
        ageDescription: '(2 - 11 años)',
        isAdult: false,
        passengerType: 'CHD',
      });
    }

    for (let i = 0; i < (passengers?.baby?.quantity || 0); i++) {
      forms.push({
        type: 'Bebé',
        ageDescription: '(0 - 23 meses)',
        isAdult: false,
        passengerType: 'INF',
      });
    }

    if (passengersFiscalData.length === 0) {
      setPassengersFiscalData(
        forms.map(() => ({
          documentType: '',
          documentNumber: '',
        }))
      );
    }

    return forms;
  }, [pricingData?.pricing?.passengers]);

  const totalPassengers = useMemo(() => {
    const passengers = pricingData?.pricing?.passengers;
    if (!passengers) return 0;

    const total =
      (passengers?.adults?.quantity || 0) +
      (passengers?.children?.quantity || 0) +
      (passengers?.baby?.quantity || 0);

    return total;
  }, [pricingData?.pricing?.passengers]);

  const {
    bookingData,
    updateContactInfo,
    updatePassengerInfo,
    updateEmergencyContact,
    updateFiscalData,
    hasErrors,
    isComplete,
  } = useBookingData(totalPassengers);

  const handlePassengerAccordionChange = (index) => {
    const newExpandedPassengers = [...expandedPassengers];
    newExpandedPassengers[index] = !newExpandedPassengers[index];

    setExpandedPassengers(newExpandedPassengers);
  };

  const handleCommonFiscalDataChange = (field, value) => {
    setCommonFiscalData((prev) => {
      const updatedData = {
        ...prev,
        [field]: value,
      };

      if (field === 'validationStatus') {
        const hasErrors = value;

        for (let i = 0; i < totalPassengers; i++) {
          updateFiscalData(i, prev, true, hasErrors);
        }

        return prev;
      }

      const updatedPassengersFiscalData = passengersFiscalData.map((_, i) => {
        updateFiscalData(i, updatedData, true, false);
        return updatedData;
      });
      setPassengersFiscalData(updatedPassengersFiscalData);

      return updatedData;
    });
  };

  const handleCommonFiscalDataToggle = (index, data) => {
    if (data) {
      setUseCommonFiscalData(true);
      setCommonFiscalDataPassengerIndex(index);
      setCommonFiscalData(data);

      const updatedPassengersFiscalData = passengersFiscalData.map((_, i) => {
        return { ...data };
      });
      setPassengersFiscalData(updatedPassengersFiscalData);

      const hasValidationErrors = !data.documentType || !data.documentNumber;

      for (let i = 0; i < totalPassengers; i++) {
        if (i !== index) {
          updateFiscalData(i, { ...data }, true, hasValidationErrors);
        } else {
          updateFiscalData(i, { ...data }, false, hasValidationErrors);
        }
      }
    } else {
      const previousCommonIndex = commonFiscalDataPassengerIndex;
      const previousCommonData = commonFiscalData;

      setUseCommonFiscalData(false);
      setCommonFiscalDataPassengerIndex(null);
      setCommonFiscalData({
        documentType: '',
        documentNumber: '',
      });

      const updatedPassengersFiscalData = Array(totalPassengers)
        .fill(null)
        .map((_, i) => {
          if (i === previousCommonIndex) {
            const passengerData = { ...previousCommonData };
            const hasValidationErrors =
              !passengerData.documentType || !passengerData.documentNumber;
            updateFiscalData(i, passengerData, false, hasValidationErrors);
            return passengerData;
          } else {
            const emptyData = { documentType: '', documentNumber: '' };
            updateFiscalData(i, emptyData, false, true);
            return emptyData;
          }
        });

      setPassengersFiscalData(updatedPassengersFiscalData);
    }
  };

  const handleIndividualFiscalDataChange = (index, field, value) => {
    setPassengersFiscalData((prev) => {
      const newData = [...prev];
      while (newData.length <= index) {
        newData.push({
          documentType: '',
          documentNumber: '',
        });
      }

      newData[index] = {
        ...newData[index],
        [field]: value,
      };
      updateFiscalData(index, newData[index], false, false);

      return newData;
    });
  };

  const handleContactSubmit = async (values) => {
    if (!values || Object.keys(values).length === 0) {
      setIsSubmitAttempted(true);
      setContactExpanded(true);
      setBaggageExpanded(false);
      setPriceExpanded(false);
      setExpandedPassengers(expandedPassengers.map(() => false));
      return;
    }

    const formattedValues = {
      ...values,
      telefono1: {
        codigo: values.telefono1_codigo,
        numero: values.telefono1_numero,
      },
      ...(values.telefono2_codigo &&
        values.telefono2_numero && {
          telefono2: {
            codigo: values.telefono2_codigo,
            numero: values.telefono2_numero,
          },
        }),
    };

    const isValid = await contactFormRef.current?.validate();

    if (!isValid) {
      setIsSubmitAttempted(true);
      setContactExpanded(true);
      setBaggageExpanded(false);
      setPriceExpanded(false);
      setExpandedPassengers(expandedPassengers.map(() => false));
      return;
    }

    setContactExpanded(false);

    if (generatePassengerForms.length > 0) {
      const newExpandedPassengers = expandedPassengers.map((_, index) => index === 0);
      setExpandedPassengers(newExpandedPassengers);
    }
  };

  const handleBookingSubmit = async () => {
    setIsSubmitAttempted(true);
    let hasValidationErrors = false;

    const contactValues = contactFormRef.current?.getValues();
    const contactErrors = contactFormRef.current?.getErrors();
    const isContactValid = await contactFormRef.current?.validate();

    if (!contactValues || !isContactValid || Object.keys(contactErrors || {}).length > 0) {
      hasValidationErrors = true;
      setContactExpanded(true);
      setBaggageExpanded(false);
      setPriceExpanded(false);
      setExpandedPassengers(expandedPassengers.map(() => false));
      return;
    }

    for (let i = 0; i < generatePassengerForms.length; i++) {
      const passengerFormRef = passengerFormRefs.current[i];
      if (passengerFormRef) {
        const passengerValues = passengerFormRef.getValues();
        const passengerErrors = passengerFormRef.getErrors();
        const isPassengerValid = await passengerFormRef.validate();

        if (
          !passengerValues ||
          !isPassengerValid ||
          Object.keys(passengerErrors || {}).length > 0
        ) {
          hasValidationErrors = true;
          const newExpandedPassengers = expandedPassengers.map((_, index) => index === i);
          setExpandedPassengers(newExpandedPassengers);
          setContactExpanded(false);
          setBaggageExpanded(false);
          setPriceExpanded(false);
          return;
        }
      }
    }

    if (useCommonFiscalData) {
      if (
        flightType === 'international' &&
        (!commonFiscalData.documentType || !commonFiscalData.cuitCuil)
      ) {
        hasValidationErrors = true;
        const newExpandedPassengers = expandedPassengers.map(
          (_, i) => i === commonFiscalDataPassengerIndex
        );
        setExpandedPassengers(newExpandedPassengers);
        setContactExpanded(false);
        setBaggageExpanded(false);
        setPriceExpanded(false);
        return;
      }
    } else {
      if (flightType === 'international') {
        const hasIncompleteFiscalData = passengersFiscalData.some(
          (data) => !data.documentType || !data.cuitCuil
        );

        if (hasIncompleteFiscalData) {
          hasValidationErrors = true;
          const firstIncompleteIndex = passengersFiscalData.findIndex(
            (data) => !data.documentType || !data.cuitCuil
          );
          const newExpandedPassengers = expandedPassengers.map(
            (_, i) => i === firstIncompleteIndex
          );
          setExpandedPassengers(newExpandedPassengers);
          setContactExpanded(false);
          setBaggageExpanded(false);
          setPriceExpanded(false);
          return;
        }
      }
    }

    if (!hasValidationErrors) {
      try {
        setModalState((prev) => ({
          ...prev,
          open: true,
          status: 'loading',
          errorMessage: '',
        }));

        const jsonModel = generateBookingModel(bookingData, flightInfo, selectedBaggage);
        const result = await createBooking(jsonModel);

        const errorHandler = (result) => {
          if (result?.errors?.length > 0) {
            result.errors.forEach((error) => {
              throw new Error(
                error?.description ||
                  'No se pudo crear la reserva, intente nuevamente o contacte a soporte'
              );
            });
          } else {
            throw new Error(
              result?.error?.message ||
                'No se pudo crear la reserva, intente nuevamente o contacte a soporte'
            );
          }
        };

        if (!result?.data?.booking?.id) {
          errorHandler(result);
        }

        if (result?.success) {
          const emailResponse = await sendEmailInfo(
            {
              email: bookingData.contactInfo.data?.email,
              name: bookingData.contactInfo.data?.name,
              surnames: bookingData.contactInfo.data?.surnames,
              phone: bookingData.contactInfo.telefono1,
            },
            bookingData,
            pricingData,
            result.data,
            selectedBaggage
          );

          setModalState((prev) => ({
            ...prev,
            status: 'success',
            bookingId: result?.data?.booking?.id,
            emailResponse,
          }));
          setIsSubmitAttempted(false);
        } else {
          throw new Error(result?.error?.message || 'No se pudo crear la reserva');
        }
      } catch (error) {
        setModalState((prev) => ({
          ...prev,
          status: 'error',
          errorMessage: error.message || 'Ha ocurrido un error al procesar su reserva',
        }));
        setIsSubmitAttempted(false);
      }
    }
  };

  const handleErrorProcessed = () => {
    setIsSubmitAttempted(false);
  };

  const navigateToErrorSection = (section, index = null) => {
    switch (section) {
      case 'contact':
        setContactExpanded(true);
        setBaggageExpanded(false);
        setPriceExpanded(false);
        setExpandedPassengers(expandedPassengers.map(() => false));

        setTimeout(() => {
          const contactElement = document.getElementById('contact-section');
          if (contactElement) {
            contactElement.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          }
        }, 100);
        break;

      case 'passenger':
        if (index !== null && index >= 0 && index < expandedPassengers.length) {
          const newExpandedPassengers = expandedPassengers.map((_, i) => i === index);
          setExpandedPassengers(newExpandedPassengers);
          setContactExpanded(false);
          setBaggageExpanded(false);
          setPriceExpanded(false);

          setTimeout(() => {
            const passengerElement = document.getElementById(`passenger-section-${index}`);
            if (passengerElement) {
              passengerElement.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
              });
            }
          }, 100);
        }
        break;

      case 'fiscal':
        let passengerToExpand = 0;

        if (
          bookingData.fiscalData.useCommonData &&
          bookingData.fiscalData.commonDataPassengerIndex !== null
        ) {
          passengerToExpand = bookingData.fiscalData.commonDataPassengerIndex;
        }

        const newExpandedPassengers = expandedPassengers.map((_, i) => i === passengerToExpand);
        setExpandedPassengers(newExpandedPassengers);
        setContactExpanded(false);
        setBaggageExpanded(false);
        setPriceExpanded(false);

        setTimeout(() => {
          const fiscalElement = document.querySelector(
            `#passenger-section-${passengerToExpand} .MuiAccordionDetails-root`
          );
          if (fiscalElement) {
            fiscalElement.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
            const fiscalContainer = fiscalElement.querySelector(
              '[data-testid="fiscal-data-container"]'
            );
            if (fiscalContainer) {
              fiscalContainer.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
              });
            }
          }
        }, 300);
        break;

      default:
        break;
    }
  };

  const handleBackToResults = () => {
    if (!backUrlRef.current) {
      const previousParams = getSessionStorageItem('previousSearchUrl') || '';
      backUrlRef.current = `${ROUTE_KEYS.BUSCAR_X_VUELOS}${previousParams}`;
    }
    return backUrlRef.current;
  };

  const backToResultsUrl = useMemo(() => handleBackToResults(), []);

  const handleBackArrow = useCallback(() => {
    if (isClient) {
      window.location.href = backToResultsUrl;
    }
  }, [backToResultsUrl]);

  useHandleBrowserBackButton(backToResultsUrl);

  useEffect(() => {
    const hasShownNotification = safeSessionStorage.getItem('backNavigationNotified');

    if (!hasShownNotification) {
      safeSessionStorage.setItem('backNavigationNotified', 'true');
    }
  }, []);

  useEffect(() => {
    fetchPricingData();
    scrollToTop();
  }, []);

  useEffect(() => {
    if (
      generatePassengerForms.length > 0 &&
      (!expandedPassengers.length || expandedPassengers.length !== generatePassengerForms.length)
    ) {
      const initialExpanded = Array(generatePassengerForms.length).fill(false);
      initialExpanded[0] = true;
      setExpandedPassengers(initialExpanded);
    }
  }, [generatePassengerForms.length]);

  return (
    <Container maxWidth='lg'>
      <Box sx={{ py: 4 }}>
        <Typography gutterBottom sx={{ mb: 4 }} variant='h4'>
          {`Reserva de vuelo ${originCode} - ${destinationCode}`}
        </Typography>

        <WarningsModal
          handleBackToResults={handleBackToResults}
          onAccept={handleAcceptPriceChange}
          onClose={handleCloseWarningsModal}
          open={warningsModalState.open}
          warnings={warningsModalState.warnings}
        />

        <CustomAccordion
          expanded={flightExpanded}
          hasError={hasError}
          icon={FlightOutlinedIcon}
          isLoading={isLoading}
          onChange={() => setFlightExpanded(!flightExpanded)}
          onRetry={fetchPricingData}
          subtitle={`${originCityName} - ${destinationCityName}`}
          title='Detalle del vuelo'
        >
          <FlightSingleCard flightData={pricingData?.pricing} />
        </CustomAccordion>

        <AttentionAlert />

        {generatePassengerForms.map((passenger, index) => {
          const passengerSubtitle = passenger
            ? `${passenger.type} ${passenger.ageDescription}`
            : '';

          const isExpanded = expandedPassengers[index] === true;

          return (
            <CustomAccordion
              expanded={isExpanded}
              hasError={hasError}
              icon={PersonOutlineOutlinedIcon}
              id={`passenger-section-${index}`}
              isLoading={isLoading}
              key={`passenger-${index}`}
              onChange={() => handlePassengerAccordionChange(index)}
              onRetry={fetchPricingData}
              subtitle={passengerSubtitle}
              title={`Pasajero ${index + 1}`}
            >
              <PassengerForm
                commonFiscalData={commonFiscalData}
                commonFiscalDataPassengerIndex={commonFiscalDataPassengerIndex}
                handleCommonFiscalDataChange={handleCommonFiscalDataChange}
                handleCommonFiscalDataToggle={handleCommonFiscalDataToggle}
                handleIndividualFiscalDataChange={handleIndividualFiscalDataChange}
                index={index}
                isInternational={flightType === 'international'}
                passenger={passenger}
                passengerConfig={pricingData?.passengerConfig}
                passengersFiscalData={passengersFiscalData}
                ref={handlePassengerFormRef(index)}
                totalPassengers={flightInfo?.passengers?.length || 0}
                updateEmergencyContact={updateEmergencyContact}
                updateFiscalData={updateFiscalData}
                updatePassengerInfo={updatePassengerInfo}
                useCommonFiscalData={useCommonFiscalData}
              />
            </CustomAccordion>
          );
        })}

        <CustomAccordion
          expanded={baggageExpanded}
          hasError={hasError}
          icon={LuggageOutlinedIcon}
          isLoading={isLoading}
          onChange={() => setBaggageExpanded(!baggageExpanded)}
          onRetry={fetchPricingData}
          title='Equipaje'
        >
          <BaggageForm
            destinationCityName={destinationCityName}
            destinationCode={destinationCode}
            flightData={pricingData?.pricing}
            onBaggageSelect={handleBaggageSelection}
            originCityName={originCityName}
            originCode={originCode}
            services={pricingData?.standalone}
          />
        </CustomAccordion>

        <CustomAccordion
          expanded={contactExpanded}
          hasError={hasError}
          icon={PhoneOutlinedIcon}
          id='contact-section'
          isLoading={isLoading}
          onChange={() => setContactExpanded(!contactExpanded)}
          onRetry={fetchPricingData}
          title='Contacto de reserva'
        >
          <ContactForm
            contactConfig={pricingData?.pricing?.formConfiguration?.contact}
            onSubmit={handleContactSubmit}
            ref={contactFormRef}
            updateContactInfo={updateContactInfo}
          />
        </CustomAccordion>

        <CustomAccordion
          expanded={priceExpanded}
          hasError={hasError}
          icon={AttachMoneyOutlinedIcon}
          isLoading={isLoading}
          onChange={() => setPriceExpanded(!priceExpanded)}
          onRetry={fetchPricingData}
          summarySubtitle={formatPrice(totalPrice, pricingData?.pricing?.price?.currency, true)}
          title='Desglose de precio'
        >
          <PriceBreakdown
            flightData={pricingData?.pricing}
            onTotalPriceChange={handleTotalPriceChange}
            selectedBaggage={selectedBaggage}
          />
        </CustomAccordion>

        <InfoAlert />

        <BookingStatusModal
          bookingId={modalState.bookingId}
          cleanSessionStorage={cleanSessionStorage}
          errorMessage={modalState.errorMessage}
          handleBackArrow={handleBackArrow}
          onClose={handleCloseModal}
          open={modalState.open}
          redirectDelay={30000}
          status={modalState.status}
        />

        <FormErrorHandler
          bookingData={bookingData}
          contactFormRef={contactFormRef}
          isSubmitAttempted={isSubmitAttempted}
          onErrorProcessed={handleErrorProcessed}
          onNavigateToSection={navigateToErrorSection}
          passengerFormRefs={passengerFormRefs}
        />

        <Stack justifyContent='space-between' spacing={2} sx={{ mt: 4, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: 2 }}>
          <Button
            onClick={handleBackArrow}
            size='large'
            sx={{
              borderColor: background,
              color: background,
              backgroundColor: 'transparent',
              minWidth: '215px',
              '&:hover': {
                borderColor: background,
                color: background,
                backgroundColor: 'transparent',
                opacity: 0.7,
              },
              textDecoration: 'none',
            }}
            variant='outlined'
          >
            Volver a resultados
          </Button>
          <Button
            disabled={modalState.open && modalState.status === 'loading'}
            onClick={handleBookingSubmit}
            size='large'
            sx={{
              backgroundColor: background,
              color: text,
              minWidth: '215px',
              '&:hover': {
                backgroundColor: background,
                color: text,
                opacity: 0.7,
              },
              '&.Mui-disabled': {
                backgroundColor: 'rgba(0, 0, 0, 0.12)',
                color: 'rgba(0, 0, 0, 0.26)',
              },
            }}
            variant='contained'
          >
            Reservar
          </Button>
        </Stack>
      </Box>
    </Container>
  );
};

export default BookingPage;
